import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AppShell, { forgetNavCounts } from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { OrgNotes } from '../components/organisation/OrgNotes';
import { OrgLogo } from '../components/OrgLogo';
import { OrgStatusPill } from '../components/StatusPills';
import { TimelineView } from '../components/mylife/TimelineView';
import { RecordDetail } from '../components/mylife/RecordDetail';
import { analysisSettled } from '../components/organisation/OrgSummary';
import { OrgCabinet } from '../components/organisation/OrgCabinet';
import { OrgNoticed } from '../components/organisation/OrgNoticed';
import { OrgTimeHere } from '../components/organisation/OrgTimeHere';
import { OrgData } from '../components/organisation/OrgData';
import { NationLine } from '../components/organisation/NationLine';
import { UploadDocumentDialog } from '../components/UploadDocumentDialog';
import { AnalyseDocumentDialog } from '../components/AnalyseDocumentDialog';
import { useLazyRecords } from '../hooks/useLazyRecords';
import { useOrgEditing } from '../hooks/useOrgEditing';
import { applyCloser, visibleDismissed } from '../lib/findings';
import { organisationsApi } from '../lib/api';
import { getOrgType } from '../lib/orgTypes';
import { spanWords, ageAt, rangeCaps, schoolYearsFor } from '../lib/orgProfile';
import { getActingOnBehalfOf } from '../lib/sharedAccess';

const UNANALYSED_STATUSES = new Set([undefined, null, 'not_started', 'failed', 'partial']);

const TABS = [
  { id: 'time', label: 'Your time here' },
  { id: 'records', label: 'Records' },
  { id: 'noticed', label: 'What we noticed' },
  { id: 'data', label: 'Requests & data' },
  { id: 'notes', label: 'My notes' },
];
// Links made before the page was rearranged still open the right place.
const OLD_TABS = { overview: 'time', details: 'time', cabinet: 'records', timeline: 'records', request: 'data' };

// How long, said the way this kind of organisation was part of your life.
const timeSentence = (org, dob) => {
  const span = spanWords(org.start_date, org.end_date);
  if (!span) return null;
  const ongoing = !org.end_date;
  const from = ageAt(dob, org.start_date);
  const to = ongoing ? null : ageAt(dob, org.end_date);
  const ages = from !== null && to !== null && to > from ? `, from when you were ${from} until you were ${to}`
    : from !== null && ongoing ? `, since you were ${from}` : '';
  const lead = {
    council: 'Responsible for you for', fostering_agency: 'Responsible for you for', adoption: 'Involved with you for',
    childrens_home: 'You lived here for', school: 'You were here for', doctor: 'Registered with them for',
    hospital: 'Seeing them over', mental_health: 'Seeing them over', police: 'Involved with you over',
  }[org.type] || 'Your time with them:';
  return { lead, span: `${span}${ongoing ? ' so far' : ''}`, rest: ages };
};

/**
 * One organisation, about the person's time there first: how long, the
 * homes, the people, what stood out ("Your time here"); then the file
 * itself (Records), what an automated check and a closer read noticed,
 * the requests and everything about the data (Requests & data), and their
 * own notes. The tab lives in the URL (?tab=) so a link opens there.
 */
export const OrganisationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const asked = OLD_TABS[params.get('tab')] || params.get('tab');
  const tab = TABS.some((t) => t.id === asked) ? asked : 'time';
  const setTab = (t) => setParams((p) => { const n = new URLSearchParams(p); n.set('tab', t); return n; }, { replace: true });

  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cabinet, setCabinet] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [gapStatus, setGapStatus] = useState(null);
  const [recordFindings, setRecordFindings] = useState([]);
  // What the person dismissed, each with its reason — the "Dismissed"
  // drawer of What we noticed, where any can be put back.
  const [gapsDismissed, setGapsDismissed] = useState([]);
  const [recordDismissed, setRecordDismissed] = useState([]);
  // Findings new since the person last had What we noticed open — the
  // organisation's "something new" dot, carried through to the tab and each
  // card. Collected from every answer this visit and never dropped until
  // they leave, so marking them seen doesn't make the "New" vanish mid-read.
  const [newIds, setNewIds] = useState(() => new Set());
  const [noticedVisited, setNoticedVisited] = useState(false);
  const addNew = useCallback((list) => {
    const ids = (list || []).filter((f) => f.is_new).map((f) => f.id);
    if (ids.length) setNewIds((prev) => new Set([...prev, ...ids]));
  }, []);
  // The last closer read: its consolidated points and which checks they absorbed.
  const [closer, setCloser] = useState(null);
  const [nation, setNation] = useState({ nation: null, source: null });
  const [review, setReview] = useState({ state: 'idle' });
  const [sars, setSars] = useState([]);
  const [profile, setProfile] = useState(null);
  const [profileState, setProfileState] = useState('idle');
  const [recordsView, setRecordsView] = useState(params.get('tab') === 'timeline' ? 'date' : 'kind');
  const [drawer, setDrawer] = useState(null);
  const [type, setType] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [analyseOpen, setAnalyseOpen] = useState(false);

  // Record-level findings, plus the last closer read (kept encrypted). The
  // closer read also runs by itself after analysis; while it's reading, the
  // page says so and checks back (see the effect below).
  const [findingsLoaded, setFindingsLoaded] = useState(false);
  const applyRecordFindings = useCallback((f) => {
    setFindingsLoaded(true);
    // Includes what only shows beside another organisation's records (a
    // concern the school raised that this council's records don't show).
    const closer = f.data?.closer_read;
    setRecordFindings(f.data?.findings || []);
    setRecordDismissed(f.data?.dismissed || []);
    addNew([...(f.data?.findings || []), ...(closer?.findings || [])]);
    if (!closer) return;
    // While it's reading again, the last read's points stay on screen.
    if (closer.status !== 'reading' || closer.findings.length) setCloser({ findings: closer.findings, absorbed: closer.absorbed || [] });
    setReview((r) => {
      if (r.state === 'running') return r;
      if (closer.status === 'reading') return { state: 'auto', count: closer.findings.length, writtenAt: closer.written_at };
      if (closer.status === 'failed' && !closer.written_at) return { state: 'idle' };
      return {
        state: 'done', count: closer.findings.length, read: closer.records_read || 0,
        total: closer.records_total || 0, partsFailed: closer.parts_failed || 0, writtenAt: closer.written_at,
      };
    });
  }, [addNew]);

  const loadFindings = useCallback(() => {
    organisationsApi.getGaps(id)
      .then((g) => {
        setGaps(g.data?.gaps || []); setGapsDismissed(g.data?.dismissed || []); setGapStatus(g.data?.status || null);
        addNew(g.data?.gaps);
        setNation({ nation: g.data?.nation || null, source: g.data?.nation_source || null });
      })
      .catch(() => setGaps([]));
    organisationsApi.getRecordFindings(id)
      .then(applyRecordFindings)
      .catch(() => setRecordFindings([]));
  }, [id, applyRecordFindings, addNew]);

  // What the records say about the time there — who's named and when,
  // records per home. Needs the key; without it the tab shows what the
  // person told us and says so.
  const loadProfile = useCallback((o) => {
    if (!o?.document_count && !(o?.documents || []).length) { setProfile(null); setProfileState('none'); return; }
    setProfileState('loading');
    organisationsApi.getProfile(id)
      .then((r) => {
        if (r.data?.locked) { setProfileState('locked'); return; }
        setProfile(r.data); setProfileState('ready');
      })
      .catch(() => setProfileState('error'));
  }, [id]);
  const reloadOrg = useCallback(async () => {
    const d = await organisationsApi.getDashboard(id);
    setOrg(d.data.organisation);
  }, [id]);
  const editing = useOrgEditing(org, reloadOrg);

  const load = useCallback(async () => {
    try {
      const [d, c, s] = await Promise.all([
        organisationsApi.getDashboard(id),
        organisationsApi.getCabinet(id).catch(() => ({ data: null })),
        organisationsApi.getSars(id).catch(() => ({ data: { sars: [] } })),
      ]);
      setOrg(d.data.organisation);
      setCabinet(c.data);
      setSars(s.data?.sars || []);
      loadFindings();
      loadProfile(d.data.organisation);
    } catch {
      toast.error('This organisation could not be loaded.');
      navigate('/organisations');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  // Opening the organisation clears its "something new" dot (and the nav's).
  useEffect(() => {
    organisationsApi.markViewed(id).then(forgetNavCounts).catch(() => {});
  }, [id]);

  const dismissFinding = useCallback(async (finding, reason) => {
    const findingId = finding.id;
    // A consolidated point takes the checks it absorbed with it, so a later
    // read that words things differently doesn't bring them back.
    const point = (closer?.findings || []).find((f) => f.id === findingId);
    const ids = [findingId, ...(point?.merges || [])];
    const fromGaps = (gaps || []).some((g) => g.id === findingId);
    (fromGaps ? setGapsDismissed : setRecordDismissed)((prev) => [{ ...finding, dismissed_reason: reason }, ...prev]);
    setGaps((prev) => (prev || []).filter((g) => !ids.includes(g.id)));
    setRecordFindings((prev) => prev.filter((g) => !ids.includes(g.id)));
    setCloser((c) => (c ? { ...c, findings: c.findings.filter((g) => g.id !== findingId) } : c));
    try {
      await Promise.all(ids.map((fid) => organisationsApi.dismissFinding(id, fid, reason)));
    } catch {
      toast.error('Could not dismiss that — refresh and try again.');
      load();
    }
  }, [id, load, closer, gaps]);

  // Put a dismissed finding back (with the checks a point absorbed), then
  // ask again so it lands wherever it belongs now.
  const restoreFinding = useCallback(async (finding) => {
    const ids = [finding.id, ...(finding.merges || [])];
    setGapsDismissed((prev) => prev.filter((g) => !ids.includes(g.id)));
    setRecordDismissed((prev) => prev.filter((g) => !ids.includes(g.id)));
    try {
      await Promise.all(ids.map((fid) => organisationsApi.restoreFinding(id, fid)));
    } catch {
      toast.error('Could not put that back — refresh and try again.');
    }
    loadFindings();
  }, [id, loadFindings]);
  // Which organisation is on screen now — a closer read that finishes after
  // the person has moved on must not land on (or be dismissed against) the
  // wrong one.
  const currentId = useRef(id);
  useEffect(() => {
    currentId.current = id; setReview({ state: 'idle' }); setCloser(null); setFindingsLoaded(false); setProfile(null);
    setNewIds(new Set()); setNoticedVisited(false);
  }, [id]);
  useEffect(() => {
    if (review.state !== 'auto') return undefined;
    const t = setInterval(() => {
      organisationsApi.getRecordFindings(id).then(applyRecordFindings).catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [review.state, id, applyRecordFindings]);
  const closerRead = useCallback(async () => {
    const startedFor = id;
    setReview({ state: 'running' });
    try {
      const r = await organisationsApi.reviewFile(startedFor, ({ done, steps }) => {
        if (currentId.current === startedFor) setReview({ state: 'running', done, steps });
      });
      if (currentId.current !== startedFor) return;
      if (r.data?.locked) { setReview({ state: 'locked' }); return; }
      const found = r.data?.findings || [];
      if (!r.data?.error && r.data?.available !== false) setCloser({ findings: found, absorbed: r.data?.absorbed || [] });
      setReview({
        state: r.data?.available === false ? 'unavailable' : r.data?.error ? 'error' : 'done',
        count: found.length, read: r.data?.records_read || 0, total: r.data?.records_total || 0,
        partsFailed: r.data?.parts_failed || 0, writtenAt: r.data?.written_at,
      });
    } catch {
      if (currentId.current === startedFor) setReview({ state: 'error' });
    }
  }, [id]);
  // There's no button for a closer read: it runs by itself whenever this
  // organisation's documents finish analysis. If there's no kept read yet
  // (or the last one failed) once analysis has settled — e.g. from before AI
  // was set up — start one here, once per visit, since only the browser
  // holds the key.
  const autoReadFor = useRef(null);
  const settled = analysisSettled(org?.documents);
  const hasRecords = (cabinet?.totals?.records || 0) > 0;
  useEffect(() => {
    if (!findingsLoaded || !settled || !hasRecords || review.state !== 'idle' || autoReadFor.current === id) return;
    autoReadFor.current = id;
    closerRead();
  }, [findingsLoaded, settled, hasRecords, review.state, id, closerRead]);

  // The closer read's consolidated points first, then the checks it didn't
  // absorb — plaintext (type/year), then from inside the records. Only findings with something concrete to ask for feed the
  // follow-up composer — a pattern in how you were written about isn't
  // something a letter can request.
  const allFindings = gaps === null ? null : applyCloser([...gaps, ...recordFindings], closer);
  const followUpGaps = (allFindings || []).filter((g) => !!g.request_text);
  const dismissedFindings = visibleDismissed([...recordDismissed, ...gapsDismissed], closer);
  const newCount = (allFindings || []).filter((f) => newIds.has(f.id)).length;

  // With What we noticed open, whatever's on it has been seen: mark it (the
  // owner only — someone they share with looking doesn't count), including
  // anything a closer read adds while it's open.
  const onScreenKey = tab === 'noticed' && allFindings ? allFindings.map((f) => f.id).sort().join('|') : '';
  useEffect(() => {
    if (tab === 'noticed') setNoticedVisited(true);
  }, [tab]);
  useEffect(() => {
    if (!onScreenKey || getActingOnBehalfOf()) return;
    organisationsApi.markFindingsSeen(id, onScreenKey.split('|')).catch(() => {});
  }, [onScreenKey, id]);

  // Records for this organisation only — skeleton first, decrypting as you go.
  const filter = useMemo(() => (r) => (r.organisation_ids || []).includes(id), [id]);
  const { records, pendingCount, onVisible, searchResults, searching } = useLazyRecords({ filter, search: tab === 'records' && recordsView === 'kind' ? search : '' });

  const unanalysedCount = (org?.documents || []).filter((d) => UNANALYSED_STATUSES.has(d.analysis_status)).length;
  const typeInfo = org ? getOrgType(org.type) : null;
  const time = org ? timeSentence(org, editing.dob) : null;
  const years = org?.type === 'school' ? schoolYearsFor(org, profile) : [];
  const range = org ? rangeCaps(org.start_date, org.end_date) : null;
  const openNoticed = (cat) => setParams((p) => {
    const n = new URLSearchParams(p); n.set('tab', 'noticed');
    if (cat) n.set('n', cat); else n.delete('n');
    return n;
  }, { replace: true });

  // Only what's on the person: a deadline missed, or nothing asked for yet.
  const primaryAction = () => {
    if (!org || org.unresolved) return null;
    if (org.sar_status === 'overdue') return <Button variant="breach" onClick={() => setTab('data')} data-testid="org-primary-chase">Chase them</Button>;
    if (!sars.length && !(org.documents || []).length) return <Button onClick={() => setTab('data')} data-testid="org-primary-request">Send a request</Button>;
    return null;
  };

  if (loading || !org) {
    return (
      <AppShell>
        <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-ink-55" aria-label="Loading" /></div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <main className="space-y-7">
        <Button variant="ghost" size="sm" onClick={() => navigate('/organisations')} className="-ml-2" data-testid="back-to-orgs-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" aria-hidden="true" /> Back to organisations
        </Button>

        {/* Hero */}
        <header className="flex flex-col sm:flex-row sm:items-start gap-5">
          <OrgLogo orgId={org.id} website={org.website} FallbackIcon={typeInfo?.icon} size={72} />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] text-ink" data-testid="org-page-name">{org.name}</h1>
              <OrgStatusPill status={org.org_status} reason={org.org_status_reason} daysRemaining={org.days_remaining} />
            </div>
            <p className="text-base text-ink-55">
              {org.unresolved ? `Holding place for homes${org.location ? ` in ${org.location}` : ''}` : (typeInfo?.label || org.type)}
              {org.location && !org.unresolved ? ` · ${org.location}` : ''}
            </p>
            {years.length > 1 && (
              <p className="text-[19px] font-medium text-ink max-w-[70ch]" data-testid="org-time-sentence">
                You were here for <strong>{years.length} school years</strong>, {years[0].label} to {years[years.length - 1].label}{time?.rest || ''}.
              </p>
            )}
            {!years.length && time && (
              <p className="text-[19px] font-medium text-ink max-w-[70ch]" data-testid="org-time-sentence">
                {time.lead} <strong>{time.span}</strong>{time.rest}.
              </p>
            )}
            {range && <p className="pd-mono">{range}</p>}
            {!org.unresolved && !range && !years.length && (
              <p className="text-[15px] text-ink-55">When were you with them? <button type="button" onClick={() => navigate(`/organisations?edit=${org.id}`)} className="font-semibold text-brand hover:text-brand-ink">Add the dates</button></p>
            )}
            {org.unresolved && <p className="text-base text-ink-70 max-w-[70ch]">You didn’t know which council placed you here. Name it below — your records usually say.</p>}
          </div>
          <div className="flex gap-2 shrink-0">
            {primaryAction()}
            {!org.unresolved && <Button variant="secondary" onClick={() => navigate(`/organisations?edit=${org.id}`)} data-testid="edit-org-detail-btn">Edit details</Button>}
          </div>
        </header>

        <Tabs value={tab} onValueChange={setTab} className="space-y-7">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList aria-label="Sections" className="w-max">
              {TABS.map((t) => (
                <TabsTrigger key={t.id} value={t.id} className="px-3 sm:px-4" data-testid={`org-tab-${t.id}`}>
                  {t.label}
                  {t.id === 'noticed' && newCount > 0 && !noticedVisited && (
                    <span className="ml-2 w-[8px] h-[8px] rounded-full bg-brand" role="img" aria-label={`${newCount} new`} data-testid="org-tab-noticed-new" />
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="time" className="mt-0">
            <OrgTimeHere
              org={org} cabinet={cabinet} profile={profile} profileState={profileState} editing={editing}
              findings={allFindings} newFindings={newCount} gapStatus={gapStatus} loadingFindings={gaps === null}
              review={review} unanalysedCount={unanalysedCount}
              onOpenTab={setTab} onOpenNoticed={openNoticed} onOpenRecord={setSelectedRecordId}
              onUpload={() => setUploadOpen(true)} onAnalyse={() => setAnalyseOpen(true)}
            />
          </TabsContent>

          <TabsContent value="records" className="mt-0 space-y-6">
            <div role="radiogroup" aria-label="Arrange the records" className="inline-flex gap-1 rounded-full bg-ground p-1">
              {[['kind', 'By kind'], ['date', 'By date']].map(([v, l]) => (
                <button key={v} type="button" role="radio" aria-checked={recordsView === v} onClick={() => setRecordsView(v)} data-testid={`records-view-${v}`}
                  className={`h-10 px-4 rounded-full text-[15px] transition-colors duration-state ${recordsView === v ? 'bg-surface text-ink font-semibold shadow-sm' : 'text-ink-70 hover:text-ink'}`}>
                  {l}
                </button>
              ))}
            </div>
            {recordsView === 'kind' ? (
              <OrgCabinet
                records={records} onVisible={onVisible} pendingCount={pendingCount} cabinet={cabinet}
                drawer={drawer} onDrawer={setDrawer} type={type} onType={setType}
                search={search} onSearch={setSearch} searchResults={searchResults} searching={searching}
                onSelect={setSelectedRecordId}
              />
            ) : records.length === 0 ? (
              <div className="rounded-lg bg-ground py-10 px-6 text-center"><p className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink mb-1">Nothing on the timeline yet</p><p className="text-base text-ink-55">Dated records from {org.name} appear here once their documents have been analysed.</p></div>
            ) : (
              <TimelineView records={records} showGaps onSelect={setSelectedRecordId} onVisible={onVisible} />
            )}
          </TabsContent>

          <TabsContent value="noticed" className="mt-0">
            <OrgNoticed
              org={org} findings={allFindings} dismissed={dismissedFindings} gapStatus={gapStatus} hasRecords={hasRecords}
              review={review} showCloserRead={hasRecords} newIds={newIds}
              onDismiss={dismissFinding} onRestore={restoreFinding} onOpenRecord={setSelectedRecordId}
              onAskForGaps={() => setTab('data')}
              nationSlot={!org.unresolved && <NationLine orgId={org.id} nation={nation.nation} source={nation.source} onSaved={loadFindings} />}
            />
          </TabsContent>

          <TabsContent value="data" className="mt-0">
            <OrgData
              org={org} sars={sars} followUpGaps={followUpGaps} cabinet={cabinet}
              unanalysedCount={unanalysedCount} onChanged={load}
              onUpload={() => setUploadOpen(true)} onAnalyse={() => setAnalyseOpen(true)}
            />
          </TabsContent>

          <TabsContent value="notes" className="mt-0">
            <OrgNotes org={org} />
          </TabsContent>
        </Tabs>
      </main>

      <RecordDetail
        recordId={selectedRecordId}
        open={!!selectedRecordId}
        onOpenChange={(o) => !o && setSelectedRecordId(null)}
        onOpenDocument={(docId, page) => navigate(`/documents?open=${docId}${page ? `&page=${page}` : ''}`)}
        onClose={() => {}}
      />

      <UploadDocumentDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        organisations={org ? [{ id: org.id, name: org.name }] : []}
        defaultOrgId={id}
        onUploaded={load}
      />

      <AnalyseDocumentDialog
        open={analyseOpen}
        onOpenChange={setAnalyseOpen}
        organisationId={id}
        totalUnanalysed={unanalysedCount}
        onDone={load}
      />
    </AppShell>
  );
};

export default OrganisationPage;
